import { supabase } from '@/lib/supabase';

const SITE_SESSION_KEY = 'karebe-site-visit-session-id';
const SITE_TRACKED_KEY = 'karebe-site-visit-tracked';
const PRODUCT_TRACKED_PREFIX = 'karebe-product-visit:';

function getSessionStorage(): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function getVisitSessionId(): string {
  const storage = getSessionStorage();

  if (!storage) {
    return crypto.randomUUID();
  }

  const existing = storage.getItem(SITE_SESSION_KEY);
  if (existing) {
    return existing;
  }

  const next = crypto.randomUUID();
  storage.setItem(SITE_SESSION_KEY, next);
  return next;
}

export async function trackSiteVisit(pathname: string): Promise<void> {
  if (!supabase) {
    return;
  }

  const storage = getSessionStorage();
  if (storage?.getItem(SITE_TRACKED_KEY) === 'true') {
    return;
  }

  const sessionId = getVisitSessionId();
  const userAgent = typeof navigator === 'undefined' ? null : navigator.userAgent;

  const { error } = await (supabase as any).rpc('track_site_visit', {
    p_session_id: sessionId,
    p_path: pathname,
    p_user_agent: userAgent,
  });

  if (error) {
    console.error('[Visits] Failed to track site visit', error);
    return;
  }

  storage?.setItem(SITE_TRACKED_KEY, 'true');
}

export async function trackProductVisit(productId: string): Promise<void> {
  if (!supabase || !productId) {
    return;
  }

  const storage = getSessionStorage();
  const trackedKey = `${PRODUCT_TRACKED_PREFIX}${productId}`;
  if (storage?.getItem(trackedKey) === 'true') {
    return;
  }

  const { error } = await (supabase as any).rpc('track_product_visit', {
    p_product_id: productId,
    p_session_id: getVisitSessionId(),
  });

  if (error) {
    console.error('[Visits] Failed to track product visit', error);
    return;
  }

  storage?.setItem(trackedKey, 'true');
}

export async function getVisitMetrics(): Promise<{
  siteVisitCount: number;
  productVisitCount: number;
}> {
  if (!supabase) {
    return { siteVisitCount: 0, productVisitCount: 0 };
  }

  const { data, error } = await (supabase as any).rpc('get_visit_metrics');

  if (error) {
    throw new Error(error.message || 'Failed to load visit metrics');
  }

  const metrics = (data || {}) as Record<string, unknown>;

  return {
    siteVisitCount: Number(metrics.site_visit_count || 0),
    productVisitCount: Number(metrics.product_visit_count || 0),
  };
}
